import { $remoteConfigStore, RemoteConfig } from '@lib/store';

type CloudinaryCrop = 'fill' | 'fit' | 'scale' | 'crop' | 'pad' | 'thumb' | 'limit';
type CloudinaryGravity = 'auto' | 'center' | 'face' | 'faces' | 'north' | 'south' | 'east' | 'west';
type CloudinaryQuality = 'auto' | 'auto:best' | 'auto:good' | 'auto:eco' | 'auto:low';
type CloudinaryFormat = 'auto' | 'webp' | 'avif' | 'png' | 'jpg' | 'jpeg';

type CloudinaryOptions = {
  width?: number;
  height?: number;
  crop?: CloudinaryCrop;
  gravity?: CloudinaryGravity;
  quality?: CloudinaryQuality;
  format?: CloudinaryFormat;
  dpr?: number | 'auto';
  background?: string;
  radius?: number | 'max';
  blur?: number;
  sharpen?: boolean;
  progressive?: boolean;
  lossy?: boolean;
};

const isCardImageOptimizationEnabled = () => $remoteConfigStore.events.getValue(RemoteConfig.CardImageOptimization);

const isCloudinaryUrl = (url: string) => {
  try {
    const { hostname, pathname } = new URL(url);
    return hostname.includes('res.cloudinary.com') && pathname.includes('/image/upload/');
  } catch {
    return false;
  }
};

const isSvgImage = (url: string) => {
  try {
    const { pathname } = new URL(url);
    return pathname.toLowerCase().endsWith('.svg');
  } catch {
    return url.toLowerCase().split('?')[0].endsWith('.svg');
  }
};

export class ImageUtils {
  static getCloudinaryImage = (url?: string | null, options: CloudinaryOptions = {}) => {
    if (!url) return '';
    if (!isCardImageOptimizationEnabled()) return url;
    if (!isCloudinaryUrl(url)) return url;
    if (isSvgImage(url)) return url;

    const transformations = [
      options.width && `w_${options.width}`,
      options.height && `h_${options.height}`,
      options.crop && `c_${options.crop}`,
      options.gravity && `g_${options.gravity}`,
      options.quality !== undefined ? `q_${options.quality}` : 'q_auto',
      options.format !== undefined ? `f_${options.format}` : 'f_auto',
      options.dpr && `dpr_${options.dpr}`,
      options.background && `b_${options.background}`,
      options.radius && `r_${options.radius}`,
      options.blur && `e_blur:${options.blur}`,
      options.sharpen && 'e_sharpen',
      options.progressive && 'fl_progressive',
      options.lossy && 'fl_lossy',
    ]
      .filter(Boolean)
      .join(',');

    if (!transformations) return url;

    return url.replace('/image/upload/', `/image/upload/${transformations}/`);
  };
}
